/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React from "react";
import { Box, Divider, Typography } from "@mui/material";
import ContentCard, { ContentCardProps } from "../../components/general/ContentCard";
import { selectRoles } from "../auth/authSlice";

import { useWindowDimensions } from "../../utils/Hooks";
import Grid from "@mui/material/Grid";
import {
    ManageAccounts,
    Archive,
    Business,
    AssignmentInd,
    AssignmentOutlined,
    LocationOnOutlined,
    Settings as SettingsIcon,
    Info as InfoIcon,
    Storage,
    Warehouse,
    CompareArrows,
    LocalShipping,
    ListAlt,
    AccountTree,
    AssignmentTurnedIn,
    Devices,
    AirportShuttle,
    Inventory,
    MoneyOff,
    KeyboardReturn,
    Assessment,
} from "@mui/icons-material";

import { useSearchParams } from "react-router-dom";
import { Nullable } from "../../utils/Interfaces";
import StyledBreadcrumbs from "../../components/general/StyledBreadcrumbs";
import { useAppSelector } from "../../app/hooks";

const Home = () => {
    const roles: string[] = useAppSelector(selectRoles);

    const { width } = useWindowDimensions();

    const [searchParams] = useSearchParams();

    // Get a specific query parameter
    const section: Nullable<string> = searchParams.get("section");

    let sectionTitles: {
        office: string;
        materials: string;
        projects: string;
        technician: string;
        admin: string;
    } = {
        office: "Service",
        materials: "Parts/Materials",
        projects: "Projects",
        technician: "Technician",
        admin: "Admin",
    };

    let cards: {
        office: ContentCardProps[];
        materials: ContentCardProps[];
        projects: ContentCardProps[];
        technician: ContentCardProps[];
        admin: ContentCardProps[];
    } = {
        office: [
            {
                title: "Work Orders",
                icon: <AssignmentOutlined fontSize="large" />,
                description: "Manage work orders.",
                url: "/work-orders?status=UN",
            },
            {
                title: "Archive",
                icon: <Archive fontSize="large" />,
                description: "View archived work orders.",
                url: "/archive",
            },
            {
                title: "Assigments",
                icon: <AssignmentInd fontSize="large" />,
                description: "Manage technician assignment priorities for work orders.",
                url: "/assignments",
            },
            {
                title: "Companies",
                icon: <Business fontSize="large" />,
                description: "Manage companies.",
                url: "/companies",
            },
            {
                title: "Service Locations",
                icon: <LocationOnOutlined fontSize="large" />,
                description: "Manage service and project locations.",
                url: "/work-sites",
            },
        ],
        projects: [
            {
                title: "Manage Projects",
                icon: <AccountTree fontSize="large" />,
                description: "Project manager's portal.",
                url: "/projects",
                unavailable: true,
            },
        ],
        materials: [
            {
                title: "Parts/Materials Database",
                icon: <Storage fontSize="large" />,
                description: "Manage the internal catalog of materials.",
                url: "/materials/database",
            },
            {
                title: "Storage Locations",
                icon: <Warehouse fontSize="large" />,
                description: "Manage inventory storage locations.",
                url: "/materials/techwise-offices",
            },
            {
                title: "Parts/Materials Transfers",
                icon: <CompareArrows fontSize="large" />,
                description: "Transfer stock between locations.",
                url: "/materials/transfers",
            },
            {
                title: "Office Restocking",
                icon: <Inventory fontSize="large" />,
                description: "Perform weekly technician restocking.",
                url: "/materials/restocking/office",
            },
            {
                title: "Technician Restocking",
                icon: <LocalShipping fontSize="large" />,
                description: "Perform weekly technician restocking.",
                url: "/materials/restocking/technician",
            },
            {
                title: "Transfer History",
                icon: <ListAlt fontSize="large" />,
                description: "View the Part/Material transfer history and generate inventory reports.",
                url: "/materials/history",
            },
            {
                title: "RMAs",
                icon: <KeyboardReturn fontSize="large" />,
                description: "View RMA processes.",
                url: "/materials/rma?status=OP",
            },
            {
                title: "Losses",
                icon: <MoneyOff fontSize="large" />,
                description: "Audit reported material losses.",
                url: "/materials/losses",
            },
            {
                title: "Reports",
                icon: <Assessment fontSize="large" />,
                description: "View material sales reports.",
                url: "/materials/reports",
            },
        ],
        technician: [
            {
                title: "Service Dashboard",
                icon: <AirportShuttle fontSize="large" />,
                description: "Access your current and recently submitted assignments.",
                url: "/tech-dash?status=IP",
            },
            {
                title: "My Assets",
                icon: <Devices fontSize="large" />,
                description: "View your assets and initiate technician-to-technician asset transfers.",
                url: "/assets/transfers",
            },
            {
                title: "My Inventory",
                icon: <Inventory fontSize="large" />,
                description: "View your parts/materials inventory.",
                url: "/my-inventory",
            },
            {
                title: "Inventory Transfer",
                icon: <CompareArrows fontSize="large" />,
                description: "Perform a technician-to-technician part/material transfer.",
                url: "/materials/tech-transfer",
            },
        ],
        admin: [
            {
                title: "Manage Users",
                icon: <ManageAccounts fontSize="large" />,
                description: "Manage user access, roles and information.",
                url: "/admin/users",
            },
            {
                title: "Manage Assets",
                icon: <Devices fontSize="large" />,
                description: "Create and manage Techwise assets.",
                url: "/assets/manage",
            },
            {
                title: "Assign Assets",
                icon: <AssignmentTurnedIn fontSize="large" />,
                description: "Assign Techwise assets to users.",
                url: "/assets/assign",
            },
            {
                title: "Global Config",
                icon: <SettingsIcon fontSize="large" />,
                description: "Change global configuration settings.",
                url: "/admin/config",
            },
            {
                title: "About",
                icon: <InfoIcon fontSize="large" />,
                description: "View portal details and documentation.",
                url: "/about",
            },
        ],
    };

    let contentOrder: {
        office: number;
        materials: number;
        projects: number;
        technician: number;
        admin: number;
    } = {
        office: 0,
        materials: 2,
        projects: 1,
        technician: 3,
        admin: 4,
    };

    let numCols: number = 12;

    if (width >= 800 && width < 1000) {
        numCols = 6;
    }
    if (width >= 1000 && width <= 1500) {
        numCols = 4;
    } else if (width > 1500) {
        numCols = 3;
    }

    let breadcrumbTitle: string =
        sectionTitles[section as keyof typeof sectionTitles] === undefined
            ? `${section}`
            : sectionTitles[section as keyof typeof sectionTitles];

    return (
        <Box sx={{ p: 0, m: 0 }}>
            {section && (
                <StyledBreadcrumbs
                    paths={[
                        { text: "Techwise Portal", route: "/" },
                        { text: breadcrumbTitle, route: `/?section=${section}` },
                    ]}
                />
            )}

            {[...roles]
                .sort(
                    (a: string, b: string) =>
                        contentOrder[a as keyof typeof contentOrder] - contentOrder[b as keyof typeof contentOrder]
                )
                .filter((role: string) => (section ? role === (section as string) : true))
                .map((role: string, index: number) => {
                    return (
                        <Box key={index}>
                            <Divider sx={{ mt: 3, mb: 2 }}>
                                <Typography fontSize={24}>{sectionTitles[role as keyof typeof sectionTitles]}</Typography>
                            </Divider>

                            <Grid sx={{ p: 0 }} container>
                                {cards[role as keyof typeof cards].map((card: ContentCardProps, jIndex: number) => {
                                    return (
                                        <Grid xs={numCols} sx={{ p: 1 }} key={jIndex} item>
                                            <ContentCard {...card} />
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </Box>
                    );
                })}
        </Box>
    );
};

export default Home;
